import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaQueriesNumbers, useMediaQuery } from '../../../../Styles/mediaQueries';
import { useLocation } from 'react-router';
import { useAppSelector } from '../../../../Store';
import { colors, css, frames } from '../../../../Styles';
import { ButtonList, FeedCurveSettingsModal, PageHeader, NewSelect, FeedCurveViewer, NewModal } from '../../../../Components';
import { NewFieldStyles, ButtonTypes, ListStyle_Types } from '../../../../Definitions'
import Graph_QL, { DynamicDataObk } from '../../../../Services/Agrisys_GqlDataCaller';
import FeedCurveSpreading from '../../../../Components/FeedCurves/feedCurveSpreading';
import { useFeedCurves } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { FeedCurveTable } from './feedCurveTable';
import { Pages } from '../../../../Navigation/Routers/root';
import { useMilestones } from '../../../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/useMilestone';
import { FeedCurve as FeedCurveValue } from "../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall"
import { MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveUpdates } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/maintainFeedCurveCall';

export default function FeedCurve() {
  const { t } = useTranslation();
  const location = useLocation();
  const [disableCurveNavigation, setDisableCurveNavigation] = useState(false);
  const [selectedFeedCurveId, setSelectedFeedCurveId] = useState<number>();
  const [showFeedCurveModal, setShowFeedCurveModal] = useState<boolean>(false);
  const [showSpreadingModal, setShowSpreadingModal] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  
  // Media Quiries
  const hideBtns = useMediaQuery(`(max-width: 1265px)`);
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);

  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
  
  // #region | Feed Curves data
  const {call: getFeedCurves, data: feedCurveState, maintainEntries: maintainFeedCurveEntries} = useFeedCurves(connection);
  const {call: getMilestones, data: milestoneState} = useMilestones(connection);

  const maintainFeedCurve = useCallback((changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurveValue, optimisticFeedCurve: FeedCurveValue}) => {
    if (maintainFeedCurveEntries) {
      maintainFeedCurveEntries(changed, deleted, created, feedCurve)
    }
  },[maintainFeedCurveEntries])
  
  const fetchedFeedCurves = useMemo(() => {
    return feedCurveState && feedCurveState[domain] ? feedCurveState[domain].data : [];
  },[domain, feedCurveState])

  
  const feedCurve = useMemo(() => {return fetchedFeedCurves.find((feedCurve) => feedCurve.feedCurveId === selectedFeedCurveId)},[fetchedFeedCurves, selectedFeedCurveId]);
  const milestoneOptions = useMemo(() => {
    return milestoneState[domain]?.data.filter((milestone) => !milestone.deleted).map((milestone) => ({label: milestone.name, id: milestone.milestone_id})) ?? []
  },[domain, milestoneState]) 

  useEffect(() => {
    if (!(feedCurveState[domain])) {
      getFeedCurves({fetchAmount: 50, ifFeedCurveBaseInfoOnly: true})
    }
  },[domain, getFeedCurves, feedCurveState])

  useEffect(() => {
    if (!(milestoneState[domain])) {
      getMilestones({fetchAmount: 50})
    }
  },[domain, getMilestones, milestoneState])

  useEffect(() => {
    if (!selectedFeedCurveId) {
      return
    }
    const containsSettings = (feedCurve?.milestones && feedCurve.milestones.length === 0) || Boolean(feedCurve?.milestones?.find((milestone) => milestone.feedPlan))
    if (!feedCurve || !feedCurve.milestones || !containsSettings) {
      getFeedCurves({ids: [selectedFeedCurveId], ifSpreading: true})
    }
    
  },[feedCurve, getFeedCurves, selectedFeedCurveId]);

  // Get options from feedCurve Data and return the first to view if there isn't any selected from the feedCurves page.
  const feedCurvesOptions = useMemo(() => {
    const options = fetchedFeedCurves.map(feedCurve => ({
      label: feedCurve.name,
      id: feedCurve.feedCurveId
    }));
    if (!selectedFeedCurveId && location.state?.params?.selected) {
      setSelectedFeedCurveId(location.state.params.selected);
    } else if (!selectedFeedCurveId && options.length) {
      setSelectedFeedCurveId(options[0].id);
    }
    return options;
  }, [fetchedFeedCurves, selectedFeedCurveId, location.state?.params?.selected]);

  useEffect(() => {
    if (location.state?.params?.selected && !selectedFeedCurveId) {
      setSelectedFeedCurveId(location.state.params.selected);
    }
  }, [location.state, selectedFeedCurveId]);
  // #endregion

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", zIndex: "999"}}>
          <div>
            <PageHeader topHeader={t(`menuHeaders.${Pages.feedCurves}`, {defaultValue: "feed curves"})} />
            <NewSelect
              isDisabled={disableCurveNavigation}
              options={feedCurvesOptions}
              onChange={(e) => setSelectedFeedCurveId(Number(e?.id ?? 0))}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyDarkPrimary
              }}
              valueOverrides={{
                defaultValue: feedCurvesOptions.find(option => option.label === feedCurve?.name)
              }}
            />
          </div>
            <ButtonList
              contents={[
                t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"}), 
                // t("feedCurvePages.feedCurve.corrections", {defaultValue: "corrections"}), 
                t("feedCurvePages.feedCurve.settings", {defaultValue: "settings"})
              ]}
              onClick={(i => {
                if (i === 0) {setShowSpreadingModal(true)} // TODO CTO: Save to db and switch section edit
                // else if (i === 1) {}
                else if (i === 1) {setShowSettingsModal(true)}
              })}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none" } : {backgroundColor: colors.greyDarkSecondary})},
              }}
              listOverrides={{
                listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
              }}
              responsiveOverrides={{
                isResponsive: !hideBtns ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
                openContent: hideBtnText ? "" : t("buttons.options", {defaultValue: "options"})
              }}
            />
        </div>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div style={{display: "grid", overflow: "hidden", height: "100%", width: "100%", gridTemplateColumns: "1fr", gridTemplateRows: (isScreenHeightMax || mobileXs) ? "1fr" : "328px 1fr"}}>
          {/* FeedCurveViewer */}
          {(isScreenHeightMax || mobileXs) ? <></>
          : <div style={{gridArea: "1/1/2/2", overflow: "hidden"}}>
              {feedCurve?.milestones &&
              <FeedCurveViewer data={feedCurve} showMilestoneLabelsBox invertedColors
            />}
          </div>}

          {/* FeedCurveTable */}
          <div style={{gridArea: (isScreenHeightMax || mobileXs) ? "1/1/2/2" : "2/1/3/2", overflow: "hidden"}}>
            {feedCurve && 
              <FeedCurveTable 
                milestoneOptions={milestoneOptions}
                setDisableCurveNavigation={setDisableCurveNavigation}
                watchFeedCurve={() => setShowFeedCurveModal(true)}
                feedCurve={feedCurve}
                maintainFeedCurve={maintainFeedCurve}
              />
            }
          </div>
        </div>
      </frames._page_inner_content>
      {/* Popup Modal for clicked feedcurve */}
      <NewModal
        modalState={!!showFeedCurveModal}
        overrides={{
          backgroundColor: colors.greyLightTertiary
        }}
        predefinedButtons={{
          onClose: () => {setShowFeedCurveModal(false)}
        }}
        header={`${feedCurve?.name}`}
        subHeader={`${t("feed.feedCurve.feedCurve", {defaultValue: "feed curve"})} ${feedCurve?.feedCurveId}`}
        mainContent={
          <div style={{padding: "0px 20px 20px 20px"}}>
            {/* FeedCurveViewer */}
            {feedCurve?.milestones && <FeedCurveViewer data={feedCurve} showComponentLabels showMilestoneLabels/>}
          </div>
        }
      />
      {/* <NewModal
        header={t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"})}
        modalState={!!showSpreadingModal}
        predefinedButtons={{
          onClose: () => { setShowSpreadingModal(false) }
        }}
        overrides={{
          // mainContentVisibleOverflow: true
        }}
        mainContent={
        <div style={{}}> */}

      {feedCurve && 
        <FeedCurveSpreading feedCurve={feedCurve} onClose={() => setShowSpreadingModal(false)} isModalOpen={showSpreadingModal}/>
      }

      {feedCurve && 
        <FeedCurveSettingsModal closeModal={() => setShowSettingsModal(false)} isModalOpen={!!showSettingsModal} feedCurve={feedCurve} />
      }
    
    </frames.Page_inner>
  );
}